// Shared "smart" JSON parsing helpers used by the JSON Beautifier and the
// JSON Escape/Unescape tools to recover a real JS object/array from messy,
// multi-level-escaped, or inconsistently-escaped JSON text (e.g. JSON pasted
// from logs, API responses, or LLM output where a JSON array/object was
// dumped into a string field).

// Custom unescape for escape sequences
export function customUnescape(str) {
  if (typeof str !== 'string') return str;
  let result = '';
  let i = 0;
  while (i < str.length) {
    let char = str[i];
    if (char === '\\' && i + 1 < str.length) {
      i++;
      let nextChar = str[i];
      switch (nextChar) {
        case '"': result += '"'; break;
        case '\\': result += '\\'; break;
        case '/': result += '/'; break;
        case 'b': result += '\b'; break;
        case 'f': result += '\f'; break;
        case 'n': result += '\n'; break;
        case 'r': result += '\r'; break;
        case 't': result += '\t'; break;
        case 'u':
          if (i + 4 < str.length) {
            const hex = str.substring(i + 1, i + 5);
            if (/^[0-9a-fA-F]{4}$/.test(hex)) {
              result += String.fromCharCode(parseInt(hex, 16));
              i += 4;
            } else {
              result += '\\u';
            }
          } else {
            result += '\\u';
          }
          break;
        default: result += '\\' + nextChar;
      }
    } else {
      result += char;
    }
    i++;
  }
  return result;
}

// Simple unescape that preserves unicode escapes in single-backslash format
export function simpleCustomUnescape(str) {
  if (typeof str !== 'string') return str;
  let result = '';
  let i = 0;
  while (i < str.length) {
    let char = str[i];
    if (char === '\\' && i + 1 < str.length) {
      i++;
      let nextChar = str[i];
      switch (nextChar) {
        case '"': result += '"'; break;
        case '\\': result += '\\'; break;
        case '/': result += '/'; break;
        case 'b': result += '\b'; break;
        case 'f': result += '\f'; break;
        case 'n': result += '\n'; break;
        case 'r': result += '\r'; break;
        case 't': result += '\t'; break;
        case 'u':
          if (i + 4 < str.length) {
            const hex = str.substring(i + 1, i + 5);
            if (/^[0-9a-fA-F]{4}$/.test(hex)) {
              // Keep unicode escape in single-backslash format instead of converting to character
              result += '\\u' + hex;
              i += 4;
            } else {
              result += '\\u';
            }
          } else {
            result += '\\u';
          }
          break;
        default: result += '\\' + nextChar;
      }
    } else {
      result += char;
    }
    i++;
  }
  return result;
}

// Collapse escape sequences to their literal character regardless of how
// many backslashes deep they are (\", \\", \\\" ... all become "; \n, \\n,
// \\\n ... all become a real newline). Used only as a last-resort recovery
// step for text whose escaping depth is inconsistent between characters
// (e.g. quotes escaped twice but newlines escaped only once - a common
// artifact of JSON-ish text typed/assembled by hand or by an LLM).
function flattenEscapesToLiteral(str) {
  return str
    .replace(/\\+"/g, '"')
    .replace(/\\+n/g, '\n')
    .replace(/\\+t/g, '\t')
    .replace(/\\+r/g, '\r');
}

// After flattening, a nested JSON array/object that was meant to live inside
// a string value (e.g. "Answer":"[...]") is left wrapped in a spurious pair
// of quotes: "Answer":"[ ... ]". This walks the text and removes exactly
// that kind of quote-wrapping around a balanced {...}/[...] span, so the
// nested structure becomes real (unquoted) JSON instead of stringified text.
function unwrapSpuriousStringWrappedJson(text) {
  let result = '';
  let i = 0;
  while (i < text.length) {
    if (text[i] === '"' && (text[i + 1] === '[' || text[i + 1] === '{')) {
      const openChar = text[i + 1];
      const closeChar = openChar === '[' ? ']' : '}';
      let depth = 0;
      let j = i + 1;
      let inStr = false;
      for (; j < text.length; j++) {
        const c = text[j];
        if (inStr) {
          if (c === '"') inStr = false;
        } else {
          if (c === '"') inStr = true;
          else if (c === openChar) depth++;
          else if (c === closeChar) {
            depth--;
            if (depth === 0) { j++; break; }
          }
        }
      }
      if (depth !== 0) {
        // No matching close bracket found - not actually a wrapped
        // structure, leave the character as-is and move on.
        result += text[i];
        i++;
        continue;
      }
      let inner = text.substring(i + 1, j);
      inner = unwrapSpuriousStringWrappedJson(inner); // handle nested spurious wraps
      result += inner;
      let end = j;
      if (text[end] === '"') end++; // swallow the matching spurious closing quote
      i = end;
    } else {
      result += text[i];
      i++;
    }
  }
  return result;
}

function tryFlattenAndUnwrapParse(str) {
  try {
    const flat = flattenEscapesToLiteral(str);
    const unwrapped = unwrapSpuriousStringWrappedJson(flat);
    const parsed = JSON.parse(unwrapped);
    return (typeof parsed === 'object' && parsed !== null) ? parsed : null;
  } catch {
    return null;
  }
}

// Recursively deep-parse any string that looks like JSON or escaped JSON
export const deepParseJsonStrings = (value) => {
  if (typeof value !== 'string') {
    if (Array.isArray(value)) return value.map(item => deepParseJsonStrings(item));
    if (typeof value === 'object' && value !== null) {
      const newObj = {};
      for (const key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          newObj[key] = deepParseJsonStrings(value[key]);
        }
      }
      return newObj;
    }
    return value;
  }

  // Only strings that plausibly *contain* JSON (or an escaped/quoted
  // string) are candidates for further unescaping - a plain string like a
  // Windows path ("C:\Users\test") must never be run through
  // customUnescape, or a literal "\t" in it gets corrupted into a real tab
  // character. And a plain numeric/boolean-looking string (e.g. an
  // identity number "10172475322") must never be type-coerced.
  const trimmed = value.trim();
  const looksLikeEmbeddedJson =
    trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"');
  if (!looksLikeEmbeddedJson) return value;

  let currentString = value;
  const maxInnerLoops = 3;
  for (let innerLoop = 0; innerLoop < maxInnerLoops; innerLoop++) {
    try {
      const parsed = JSON.parse(currentString);
      if (typeof parsed === 'object' && parsed !== null) {
        return deepParseJsonStrings(parsed);
      }
      if (typeof parsed === 'string' && parsed !== currentString) {
        // Unwrapped one more layer of string-in-string quoting; keep going.
        currentString = parsed;
        continue;
      }
      // Parsed to a number/boolean/null: don't silently change the value's
      // type - fall through to try other unescape strategies instead.
    } catch (e1) { /* not directly parseable yet */ }

    if (currentString.includes("'")) {
      let sqReplaced = currentString.replace(/'/g, '"');
      if (sqReplaced !== currentString) {
        try {
          const parsed_sq = JSON.parse(sqReplaced);
          if (typeof parsed_sq === 'object' && parsed_sq !== null) {
            return deepParseJsonStrings(parsed_sq);
          }
          if (typeof parsed_sq === 'string' && parsed_sq !== currentString) {
            currentString = parsed_sq;
            continue;
          }
        } catch (e2) { /* not this either */ }
      }
    }
    let unescaped = customUnescape(currentString);
    if (unescaped !== currentString) {
      currentString = unescaped;
      continue;
    }
    break;
  }

  // Last-resort: the string looked like embedded JSON but didn't cleanly
  // unescape through the loop above (inconsistent escape depths) - try the
  // flatten+unwrap heuristic on just this string value.
  const flattenResult = tryFlattenAndUnwrapParse(currentString);
  if (flattenResult !== null) {
    return deepParseJsonStrings(flattenResult);
  }

  return currentString;
};

// Hack: Remove all backslashes and try to parse as JSON (for log/escaped
// dump edge cases where nothing else works). Kept as a final fallback.
function tryParseWithStrippedBackslashes(str) {
  const stripped = str.replace(/\\/g, '');
  try {
    return JSON.parse(stripped);
  } catch {
    return null;
  }
}

// The main robust parse function: tries increasingly aggressive recovery
// strategies until it finds one that yields a real object/array.
export const parseInputToJavaScriptObject = (originalInputString) => {
  if (!originalInputString || !originalInputString.trim()) {
    throw new Error("Input is empty.");
  }
  let workString = originalInputString;
  if (workString.charCodeAt(0) === 0xFEFF) {
    workString = workString.substring(1);
  }
  workString = workString.trim();

  const maxOuterLoops = 5;
  let lastError = new Error("Cannot parse string: All parsing attempts failed or resulted in a non-object/array type.");

  for (let outerLoop = 0; outerLoop < maxOuterLoops; outerLoop++) {
    const prevWorkStringForLoopDetection = workString;
    let progressMadeInIteration = false;
    try {
      let parsed = JSON.parse(workString);
      if (typeof parsed === 'object' && parsed !== null)
        return deepParseJsonStrings(parsed);
      if (typeof parsed === 'string') {
        if (workString !== parsed) {
          workString = parsed;
          progressMadeInIteration = true;
          continue;
        }
      }
      lastError = new Error(`Parsed to a primitive type (${typeof parsed}), not a structured JSON object/array.`);
      break;
    } catch (e_direct_parse) {
      lastError = e_direct_parse;
    }
    if (workString.includes("'")) {
      let singleQuoteReplaced = workString.replace(/'/g, '"');
      if (singleQuoteReplaced !== workString) {
        try {
          let parsed_sq = JSON.parse(singleQuoteReplaced);
          if (typeof parsed_sq === 'object' && parsed_sq !== null)
            return deepParseJsonStrings(parsed_sq);
          if (typeof parsed_sq === 'string') {
            if (workString !== parsed_sq) {
              workString = parsed_sq;
              progressMadeInIteration = true;
              continue;
            }
          }
          lastError = new Error(`Parsed single-quote version to a primitive type (${typeof parsed_sq}).`);
          break;
        } catch (e_sq_parse) {
          lastError = e_sq_parse;
        }
      }
    }
    let customUnescapedString = customUnescape(workString);
    if (customUnescapedString !== workString) {
      workString = customUnescapedString;
      progressMadeInIteration = true;
      continue;
    }
    if (!progressMadeInIteration) {
      if (outerLoop > 0 && workString === prevWorkStringForLoopDetection) {
        lastError = new Error("Parsing loop detected: string transformations did not lead to a parsable state or further change.");
      }
      break;
    }
  }

  // Last-resort heuristics for messy/inconsistently-escaped dumps (e.g.
  // mixed escape depths between quotes and \n from LLM/log output, or a
  // JSON object whose outer quotes were stripped when it was pasted).
  const trimmedOriginal = originalInputString.trim();
  const looksLikeJson =
    (trimmedOriginal.startsWith('{') && trimmedOriginal.endsWith('}')) ||
    (trimmedOriginal.startsWith('[') && trimmedOriginal.endsWith(']'));
  if (looksLikeJson) {
    const flattenResult = tryFlattenAndUnwrapParse(trimmedOriginal);
    if (flattenResult !== null) {
      return deepParseJsonStrings(flattenResult);
    }
    const backslashCount = (trimmedOriginal.match(/\\/g) || []).length;
    if (backslashCount > 4) {
      const stripped = tryParseWithStrippedBackslashes(trimmedOriginal);
      if (stripped !== null) {
        return deepParseJsonStrings(stripped);
      }
    }
  }

  throw new Error(`Parse failed. ${lastError?.message || 'Unknown reason.'} Input (start): ${originalInputString.substring(0, 100)}`);
};

// Simple unescape for string values within parsed objects
export const simpleUnescapeStringValues = (value) => {
  if (typeof value !== 'string') {
    if (Array.isArray(value)) return value.map(item => simpleUnescapeStringValues(item));
    if (typeof value === 'object' && value !== null) {
      const newObj = {};
      for (const key in value) {
        if (Object.prototype.hasOwnProperty.call(value, key)) {
          newObj[key] = simpleUnescapeStringValues(value[key]);
        }
      }
      return newObj;
    }
    return value;
  }

  // For string values, try to see if they contain escaped JSON
  let unescaped = simpleCustomUnescape(value);
  if (unescaped !== value) {
    return unescaped;
  }
  return value;
};

// Simple unescape function - only one level of unescaping
export const parseInputToJavaScriptObjectSimple = (originalInputString) => {
  if (!originalInputString || !originalInputString.trim()) {
    throw new Error("Input is empty.");
  }
  let workString = originalInputString;
  if (workString.charCodeAt(0) === 0xFEFF) {
    workString = workString.substring(1);
  }
  workString = workString.trim();

  // Try direct parse first
  try {
    let parsed = JSON.parse(workString);
    // Apply simple unescape to string values within the parsed object
    return simpleUnescapeStringValues(parsed);
  } catch (e1) {
    // Try single quote replacement
    if (workString.includes("'")) {
      let singleQuoteReplaced = workString.replace(/'/g, '"');
      try {
        let parsed_sq = JSON.parse(singleQuoteReplaced);
        return simpleUnescapeStringValues(parsed_sq);
      } catch (e2) {
        // Continue to unescape attempt
      }
    }

    // Try simple custom unescape once
    let customUnescapedString = simpleCustomUnescape(workString);
    if (customUnescapedString !== workString) {
      try {
        let parsed = JSON.parse(customUnescapedString);
        return simpleUnescapeStringValues(parsed);
      } catch (e3) {
        // Final attempt with single quote replacement on unescaped string
        if (customUnescapedString.includes("'")) {
          let singleQuoteReplaced = customUnescapedString.replace(/'/g, '"');
          try {
            let parsed_sq = JSON.parse(singleQuoteReplaced);
            return simpleUnescapeStringValues(parsed_sq);
          } catch (e4) {
            // Fall through to error
          }
        }
      }
    }
  }

  throw new Error(`Simple parse failed. Input (start): ${originalInputString.substring(0, 100)}`);
};
